import { ChangeDetectionStrategy, Component, computed, forwardRef, input, model } from '@angular/core';
import { NG_VALUE_ACCESSOR, type ControlValueAccessor } from '@angular/forms';

export type BzCheckboxSize = 'sm' | 'md' | 'lg';

/**
 * A checkbox that works three ways: as a two-way signal binding
 * (`[(checked)]`), inside a reactive form (`formControlName`), or with
 * `ngModel`. The `model()` input provides the first, `ControlValueAccessor`
 * the other two.
 *
 * `indeterminate` renders the mixed state used by the legacy console for
 * "some rows in this group are selected". It is a display state only —
 * clicking an indeterminate checkbox resolves it to checked, matching the
 * behaviour of a native checkbox.
 */
@Component({
  selector: 'bz-checkbox',
  templateUrl: './bz-checkbox.html',
  styleUrl: './bz-checkbox.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BzCheckbox),
      multi: true,
    },
  ],
})
export class BzCheckbox implements ControlValueAccessor {
  readonly checked = model(false);
  readonly disabled = model(false);
  readonly indeterminate = input(false);

  /**
   * Accessible name for the control when nothing is projected as a visible
   * label. Without it a bare checkbox has no name at all and fails an
   * AXE check — there is no text for a screen reader to read.
   */
  readonly ariaLabel = input('');
  readonly size = input<BzCheckboxSize>('md');

  protected readonly wrapperClass = computed(() => `bz-checkbox bz-checkbox--${this.size()}`);

  private propagateChange: (value: boolean) => void = () => undefined;
  private propagateTouched: () => void = () => undefined;

  writeValue(value: boolean): void {
    this.checked.set(!!value);
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.propagateTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  protected toggle(): void {
    if (this.disabled()) {
      return;
    }

    const next = !this.checked();
    this.checked.set(next);
    this.propagateChange(next);
  }

  protected markTouched(): void {
    this.propagateTouched();
  }
}
